import { useSession } from '@rewardops/auth'

import { useApi } from '~/components/hooks/use-api'

interface UseCartActionsOptions {
  enabled?: boolean
}

export const useCartActions = ({
  enabled = true,
}: UseCartActionsOptions = {}) => {
  const { api } = useApi()
  const utils = api.useContext()
  const { data: session } = useSession()
  const {
    data: cartItems,
    isLoading: isLoadingCartItems,
    refetch: refetchCartItems,
  } = api.cart.getCartItems.useQuery(undefined, {
    useErrorBoundary: true,
    enabled: enabled && !!session,
    retry: 2,
  })

  const addNewToCart = api.cart.addOrUpdateItem.useMutation({
    onMutate: async (newItem) => {
      const previousCartData = utils.cart.getCartItems.getData()

      // Optimistically update to the new value
      utils.cart.getCartItems.setData(undefined, (old: any) => {
        if (!old)
          return {
            cartItems: [{ ...newItem.item, quantity: newItem.quantity }],
          }

        const updatedItems = old.cartItems.map((cartItem: any) => {
          if ((cartItem as any).id === (newItem.item as any).id) {
            return {
              ...cartItem,
              quantity: cartItem.quantity + newItem.quantity,
            }
          }
          return cartItem
        })

        // If the item wasn't found in the existing items, add it as a new item
        if (
          !updatedItems.some(
            (item: any) => (item as any).id === (newItem.item as any).id,
          )
        ) {
          updatedItems.push({ ...newItem.item, quantity: newItem.quantity })
        }

        return { ...old, cartItems: updatedItems }
      })

      return { previousCartData }
    },
    onError: (err, newItem, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      utils.cart.getCartItems.setData(
        undefined,
        (context as any)?.previousCartData,
      )
    },
    onSettled: () => {
      // Always refetch after error or success
      utils.cart.getCartItems.invalidate()
    },
  })

  const removeFromCart = api.cart.removeItem.useMutation({
    onMutate: async (params: any) => {
      const { id, orderToken } = params
      await utils.cart.getCartItems.cancel()

      const previousCartData = utils.cart.getCartItems.getData()

      utils.cart.getCartItems.setData(undefined, (old: any) => {
        if (!old) return { cartItems: [] }

        const updatedItems = old.cartItems.filter(
          (item: any) => (item as any).id !== id,
        )

        return { ...old, cartItems: updatedItems }
      })

      return { previousCartData }
    },
    onError(err, newPost, ctx) {
      utils.cart.getCartItems.setData(undefined, ctx?.previousCartData)
    },
    onSuccess: async () => {
      await utils.cart.getCartItems.invalidate()
      await utils.cart.getCartCheckoutDetails.invalidate()
    },
  })

  const setQuantityInCart = api.cart.setItemQuantity.useMutation({
    onMutate: async ({ item, quantity }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await utils.cart.getCartItems.cancel()

      const previousCartData = utils.cart.getCartItems.getData()

      // Optimistically update to the new value
      utils.cart.getCartItems.setData(undefined, (old: any) => {
        if (!old) return { cartItems: [{ ...item, quantity }] }

        const updatedItems = old.cartItems.map((cartItem: any) => {
          if (cartItem.orderToken === item.orderToken) {
            return {
              ...cartItem,
              quantity: quantity,
            }
          }
          return cartItem
        })

        return { ...old, cartItems: updatedItems }
      })

      return { previousCartData }
    },
    onError(err: any, newPost: any, ctx: any) {
      // If the mutation fails, use the context returned from onMutate to roll back
      utils.cart.getCartItems.setData(undefined, ctx?.previousCartData)
    },
    onSettled() {
      // Always refetch after error or success
      utils.cart.getCartItems.invalidate()
      utils.cart.getCartCheckoutDetails.invalidate()
    },
  })
  const addToCart = async ({
    item,
    quantity,
  }: {
    item: any
    quantity: number
  }) => {
    return await addNewToCart.mutateAsync({
      item,
      quantity,
    })
  }

  const isAddingToCartLoading = addNewToCart.isLoading
  const isRemovingFromCartLoading = removeFromCart.isLoading

  const addToCartHandler = async (item: any, quantity: number) => {
    // TODO: Add to cart-context
    return await addToCart({ item, quantity })
  }

  const removeItem = async (id: number, itemOrderToken: string) => {
    // TODO: Remove from cart-context
    await removeFromCart.mutateAsync({
      id: id,
      orderToken: itemOrderToken,
    })
  }

  const setItemQuantity = async (item: any, quantity: number) => {
    // TODO: Remove from cart-context
    return await setQuantityInCart.mutateAsync({
      item,
      quantity,
    })
  }

  const isSettingQuantityLoading = setQuantityInCart.isLoading

  return {
    cartItems,
    refetchCartItems,
    isLoadingCartItems,
    isAddingToCartLoading,
    isRemovingFromCartLoading,
    isSettingQuantityLoading,
    addToCartHandler,
    removeItem,
    setItemQuantity,
  }
}
